import { Box, Typography } from "@mui/material";
import { setDocumentTitle } from "~/lib/commonUtils";
import { useSlasDashboard } from "./hooks/useSlasDashboard";
import { ScopeType, SpaceOrWorkspaceScope, SpaceScope } from "~/hooks/useScope";
import Breadcrumbs, { Breadcrumb } from "~/components/Breadcrumbs/Breadcrumbs";
import { SlasRatingFilters } from "./SlasRatingFilters";
import { ScrollToTop } from "~/lib/scroll-to-top";
import { FirewatchList } from "~/components/FirewatchList";
import { EmptyState } from "~/components/empty-state/empty-state";
import { useTickets } from "~/pages/tickets/hooks";
import {
  AggregateScoreType,
  GetAggregateScoresDocument,
  GetCasesCountDocument,
  ScoreRating,
} from "~/operations";
import { useCanApplyExceptionMutation } from "~/lib/iam/actions/mondoo/policy/ExtendedHub/ApplyExceptionMutation";
import { SelectedEntityWithAggregateScoresData } from "~/components/FirewatchPage";
import {
  SelectedEntityWithFindingData,
  SetExceptionDialog,
} from "~/components/exceptions/SetExceptionDialog";
import { SelectedEntityWithException } from "~/components/exceptions/types";
import { useSelection } from "~/hooks/useSelection";
import { SelectedEntityWithTicketData } from "~/pages/tickets/types";
import { useDialogs } from "@toolpad/core";
import { useTriageToolbar } from "~/providers/triage-toolbar";
import { CreateTicketDialog } from "~/pages/tickets/components/CreateTicketDialog";
import { sortAggregateToExceptionEntitiesMrns } from "~/components/FirewatchPage/lib/getExceptionEntitiesMrns";
import { RemoveExceptionsGroupDialog } from "~/components/exceptions/RemoveExceptionsGroupDialog";
import { useExceptions } from "~/components/exceptions/use-exceptions";
import { pluralize } from "~/lib/pluralize";
import { CreateSecurityPipelinePrDialog } from "~/pages/integrations/security-pipeline/components/CreatePullRequest/CreateSecurityPipelinePRDialog";
import { useEffect } from "react";
import { TriageToolbarButtonType } from "~/components/TriageToolbar/types";
import { useSecurityPipelineIntegrations } from "~/pages/integrations/security-pipeline/hooks/useSecurityPipelineIntegrations";
import { useTicketingIntegrations } from "~/pages/tickets/hooks/useTicketingIntegrations";
import { keyBy } from "lodash-es";
import { useExceptionsButtons } from "~/components/exceptions/use-exceptions-buttons";
import { useFeatureFlags } from "~/providers/FeatureFlagsProvider";

export interface SlasDashboardPageProps {
  space: SpaceScope;
  scope: SpaceOrWorkspaceScope;
}

export function SlasDashboardPage({ space, scope }: SlasDashboardPageProps) {
  const {
    edges,
    totalCount,
    sort,
    paginateProps,
    baseUrl,
    loading,
    empty,
    activeRatings,
  } = useSlasDashboard({
    scopeMrn: scope.mrn,
  });

  const dialogs = useDialogs();
  const triageToolbar = useTriageToolbar();

  const hasApplyExceptionPermission = useCanApplyExceptionMutation(space);

  const { hasTicketingIntegration } = useTicketingIntegrations();
  const { hasSecurityPipelineIntegrations } = useSecurityPipelineIntegrations();
  const { isFeatureEnabled } = useFeatureFlags();

  const {
    selectedEntities,
    setSelectedEntities,
    isGroupChecked,
    isGroupIndeterminate,
    onGroupCheckChange,
    handleNodeClick,
    handleNodeChange,
    handleCancelClick,
  } = useSelection<
    SelectedEntityWithFindingData &
      SelectedEntityWithException &
      SelectedEntityWithTicketData &
      SelectedEntityWithAggregateScoresData
  >();

  const edgesWithMrns = edges.flatMap((edge) =>
    edge.node && scope?.mrn
      ? [
          {
            ...edge.node,
            title: edge.node.title || "",
            rating: edge.node.rating || ScoreRating.Unscored,
            mrn: edge.node.findingMrn,
            exception: edge.node.exception,
            scopeMrn: scope.mrn,
            isAffected: (edge.node?.blastRadius?.affected || 0) > 0,
            scoreType: edge?.node?.scoreType || AggregateScoreType.Check,
          },
        ]
      : [],
  );

  const edgesByMrn = keyBy(edges, "node.findingMrn");

  const {
    exceptionsButtonsAvailability,
    exceptionsButtonsVisibility,
    exceptionsButtonsTooltipText,
  } = useExceptionsButtons({
    selectedEntities: selectedEntities.map((entity) => {
      const entityWithData = edgesByMrn[entity.mrn]?.node;

      return {
        mrn: entityWithData?.findingMrn || "",
        exception: entityWithData?.exception,
      };
    }),
    target: "finding",
    showExceptionButtons: true,
    showScopeButtons: true,
  });

  const handleCreateTicketDialogOpen = () => {
    if (!space || !scope) return;

    dialogs.open(CreateTicketDialog, {
      isCombinedTicket: isFeatureEnabled("Multi-finding ticketing"),
      target: "asset",
      loading: loading || createTicketLoading,
      space: space,
      scope: scope,
      hasCreateCasePermissions: hasCreateTicketPermissions,
      onSave: (data) =>
        handleCreateCase({
          data,
          refetchQueries: [GetAggregateScoresDocument],
        }),
      selectedEntities: selectedEntities.map((entity) => {
        const entityWithData = edgesByMrn[entity.mrn]?.node;

        return {
          mrn: entityWithData?.findingMrn || "",
          scopeMrn: scope?.mrn,
        };
      }),
    });
  };

  const handleCreateRemediationPRDialogOpen = () => {
    if (!space || !scope) return;

    dialogs.open(CreateSecurityPipelinePrDialog, {
      spaceScope: space,
      aggregateScoresEdges: selectedEntities.map(
        (entity) => edgesByMrn[entity.mrn],
      ),
    });
  };

  const handleRemoveExceptionModalOpen = () => {
    dialogs.open(RemoveExceptionsGroupDialog, {
      onRemove: async (params) => {
        await handleRemoveExceptionGroups(params);
        setSelectedEntities([]);
      },
      exceptionMrns: selectedEntities
        .map((entity) => edgesByMrn[entity.mrn]?.node?.exception?.exceptionMrn)
        .filter((mrn): mrn is string => Boolean(mrn)),
    });
  };

  const handleSetExceptionModalOpen = () => {
    dialogs.open(SetExceptionDialog, {
      targetData: {
        targetTitle: `${selectedEntities.length} ${pluralize("finding", selectedEntities.length)}`,
      },
      target: "finding",
      onSave: handleSetException,
      shouldListEntities: true,
      selectedEntities: selectedEntities.map((entity) => {
        const entityWithData = edgesByMrn[entity.mrn]?.node;

        return {
          mrn: entityWithData?.findingMrn || "",
          scopeMrn: scope?.mrn,
          rating: entityWithData?.rating || ScoreRating.None,
          title: entityWithData?.title || "",
        };
      }),
    });
  };

  const { handleRemoveExceptionGroups, handleSetException } = useExceptions({
    onSetException: () => {
      setSelectedEntities([]);
    },
    scopeMrns: [space.mrn],
    ...sortAggregateToExceptionEntitiesMrns({ selectedEntities }),
    refetchQueries: [GetAggregateScoresDocument],
  });

  const {
    handleCreateTicketingIntegrationModalOpen,
    loading: createTicketLoading,
    handleCreateCase,
    hasCreateTicketPermissions,
  } = useTickets({
    createCaseRefetchQueries: [GetCasesCountDocument],
    onTicketCreate: () => setSelectedEntities([]),
  });

  useEffect(() => {
    if (selectedEntities.length > 0) {
      triageToolbar.show({
        buttons: [
          {
            type: TriageToolbarButtonType.CreateTicket,
            onClick: !hasTicketingIntegration
              ? handleCreateTicketingIntegrationModalOpen
              : handleCreateTicketDialogOpen,
            disabled: selectedEntities.some((e) => {
              return !e.isAffected;
            }),
            tooltip:
              "Mondoo cannot create tickets for findings with zero blast radius.",
          },
          ...(exceptionsButtonsVisibility.SET_EXCEPTION &&
          scope?.type === ScopeType.Space
            ? [
                {
                  type: TriageToolbarButtonType.CreateException,
                  onClick: handleSetExceptionModalOpen,
                  disabled: !exceptionsButtonsAvailability.SET_EXCEPTION,
                  tooltip: exceptionsButtonsTooltipText.SET_EXCEPTION,
                },
              ]
            : []),
          ...(exceptionsButtonsVisibility.REMOVE_EXCEPTION &&
          scope?.type === ScopeType.Space
            ? [
                {
                  type: TriageToolbarButtonType.RemoveException,
                  onClick: handleRemoveExceptionModalOpen,
                  disabled: !exceptionsButtonsAvailability.REMOVE_EXCEPTION,
                  tooltip: exceptionsButtonsTooltipText.REMOVE_EXCEPTION,
                },
              ]
            : []),
          ...(scope?.type === ScopeType.Space && hasSecurityPipelineIntegrations
            ? [
                {
                  type: TriageToolbarButtonType.CreateRemediationWorkflow,
                  onClick: handleCreateRemediationPRDialogOpen,
                  disabled: selectedEntities.some((e) => {
                    return (
                      !e.isAffected ||
                      ![
                        AggregateScoreType.Advisory,
                        AggregateScoreType.Vulnerability,
                      ].includes(e.scoreType)
                    );
                  }),
                  tooltip:
                    "Mondoo cannot create pull requests for check findings or findings with zero blast radius.",
                },
              ]
            : []),
        ],
        payload: {
          count: selectedEntities.length,
          totalCount,
          onClose: handleCancelClick,
        },
      });
    } else {
      triageToolbar.hide();
    }

    return () => {
      triageToolbar.hide();
    };
  }, [selectedEntities, scope]);

  const title = "SLAs";

  const breadcrumbs: Breadcrumb[] = [
    {
      key: "sla-dashboard",
      title,
    },
  ];

  setDocumentTitle([title, "Dashboard"]);

  return (
    <Box>
      <ScrollToTop />
      <Breadcrumbs items={breadcrumbs} />

      <Box mb={3}>
        <Typography variant="h4" component="h1" fontWeight={700}>
          {title}
        </Typography>
        {/* TODO: Need a description or just delete this. */}
        {/* <Typography variant="body1" color="text.secondary">
          Descriptive text goes here
        </Typography> */}
      </Box>

      <SlasRatingFilters activeRatings={activeRatings} slasDashUrl={baseUrl} />

      {!empty ? (
        <FirewatchList
          space={space}
          scope={scope}
          pageType={"SlaFindings"}
          data={edges}
          loading={loading}
          sort={sort}
          paginateProps={paginateProps}
          selectionProps={{
            canSelect:
              hasApplyExceptionPermission || hasCreateTicketPermissions,
            selectedEntities,
            isGroupChecked: isGroupChecked(edgesWithMrns),
            isGroupIndeterminate: isGroupIndeterminate(edgesWithMrns),
            onAllCheckboxClick: (event) => {
              onGroupCheckChange(event, edgesWithMrns);
            },
            onRowCheckboxChange: handleNodeChange,
            onRowCheckboxClick: handleNodeClick,
          }}
        />
      ) : (
        <EmptyState contentType={"SLA Findings"} />
      )}
    </Box>
  );
}
